import {
  authControllerLogin,
  authControllerLogout,
  authControllerForgotPassword,
  authControllerVerifyResetToken,
  authControllerResetPassword,
  authControllerSignup,
  authControllerVerifySignup,
} from "./api/generated/faqrunAPI";
import type {
  ForgotPasswordDto,
  ResetPasswordDto,
  SignupDto,
  VerifySignupDto,
} from "./api/generated/faqrunAPI.schemas";
import { getCsrfToken } from "./csrf";
import { ApiClientError } from "./api/api-error";

interface LoginParams {
  email: string;
  password: string;
  selectedProductId?: string;
}

export async function login(params: LoginParams): Promise<void> {
  const response = await authControllerLogin(params);
  if (response.status !== 200) {
    throw new ApiClientError(response.status, {
      title: "認証エラー",
      status: response.status,
      detail: "ログインに失敗しました",
    });
  }
  // トークンは Set-Cookie で自動設定。明示的な保存は不要
}

export async function logout(): Promise<void> {
  try {
    await authControllerLogout();
    // logout API は JwtAuthGuard で認証 + サーバー側で全トークン無効化 [RF-2]
    // refreshToken を body から送る必要はない（Cookie の access_token で認証）
  } catch {
    // Logout API failure is non-critical
  }
  if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
}

export function isAuthenticated(): boolean {
  // csrf_token は非 HttpOnly Cookie なので JS から確認可能
  // access_token の存在の代理指標として使用
  return getCsrfToken() !== null;
}

export async function forgotPassword(email: string): Promise<void> {
  const dto: ForgotPasswordDto = { email };
  await authControllerForgotPassword(dto);
}

export async function verifyPasswordResetToken(token: string): Promise<boolean> {
  const response = await authControllerVerifyResetToken({ token });
  return response.status === 200;
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const dto: ResetPasswordDto = { token, password };
  await authControllerResetPassword(dto);
}

interface SignupParams {
  email: string;
  password: string;
}

/**
 * セルフサインアップ申請（確認メール送信）
 */
export async function signup(params: SignupParams): Promise<void> {
  const dto: SignupDto = { email: params.email, password: params.password };
  const response = await authControllerSignup(dto);
  const { status } = response;

  if (status === 204) {
    return; // 正常終了
  }

  if (status === 429) {
    throw new ApiClientError(429, {
      title: "送信制限",
      status: 429,
      detail: "確認メールは 5 分間に 1 回のみ送信できます",
    });
  }

  throw new ApiClientError(status, {
    title: "サインアップエラー",
    status,
    detail: "サインアップに失敗しました",
  });
}

/**
 * サインアップトークン確認（アカウント作成 + 自動ログイン）
 */
export async function verifySignup(token: string): Promise<void> {
  const dto: VerifySignupDto = { token };
  const response = await authControllerVerifySignup(dto);

  if (response.status !== 200) {
    throw new ApiClientError(response.status, {
      title: "認証エラー",
      status: response.status,
      detail: "トークンが無効または期限切れです",
    });
  }
}

export { ApiClientError };
